import React from 'react';
import { motion } from 'framer-motion';
import DestinationsMarquee from './DestinationsMarquee';
import Testimonials from './Testimonials';
import Press from './Press';
import CTA from './CTA';

export default function Weddings() {
  return (
    <div className="bg-white">
      {/* Hero - Video Banner */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden pt-20 sm:pt-0">
        <video
          src="/main.mp4"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[15s] ease-out"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-black/55" />

        {/* Left Social Indicator */}
        <motion.div
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1 }}
          className="hidden lg:flex flex-col gap-3 absolute left-10 top-1/2 -translate-y-1/2 z-20"
        >
          {[0, 1, 2].map((dot) => (
            <span
              key={dot}
              className={`w-2 h-2 rounded-full border border-white ${dot === 0 ? 'bg-white' : 'bg-transparent'
                }`}
            />
          ))}
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-white/70 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-6 sm:mb-8"
            >
              Luxury Weddings Worldwide
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-6 sm:mb-10">
              Turning Your Vows<br />
              <span className="text-white/90">Into Timeless Memories</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-sm sm:text-base md:text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12"
            >
              Transform your love story into an extraordinary celebration. We craft bespoke luxury wedding experiences that reflect your unique journey, creating memories that transcend time and touch hearts forever.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex items-center justify-center"
            >
              <a href="/#contact" className="group relative px-8 sm:px-12 py-4 sm:py-6 border-2 border-white/80 text-white text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white hover:text-black transition-all duration-500 rounded-sm backdrop-blur-sm">
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-4 text-white group-hover:text-black transition-colors duration-300">
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                  <span className="block w-3 h-3 rounded-full border-2 border-white/70 group-hover:border-black/70 transition-colors" />
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                </span>
                BEGIN MY WEDDING STORY
                <div className="absolute inset-0 bg-white/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute bottom-10 left-10 opacity-30">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
        <div className="absolute top-20 right-20 opacity-20">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </section>

      {/* Stats - Modern Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#395958] mb-6">Our Legacy</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              Celebrating Love<br />
              <span className="text-gray-700">Across Continents</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { v: '500+', l: 'Dream Weddings Created', desc: 'Each one a unique masterpiece' },
              { v: '100%', l: 'Dreams Fulfilled', desc: 'Exceeding every expectation' },
              { v: '50+', l: 'Global Destinations', desc: 'From intimate to grand celebrations' },
            ].map((stat, i) => (
              <motion.div
                key={stat.l}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                className="group relative p-10 text-center rounded-2xl bg-gray-50 shadow-[0_25px_50px_rgba(0,0,0,0.1)] border border-gray-200 hover:shadow-[0_35px_70px_rgba(0,0,0,0.15)] transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="text-4xl md:text-5xl lg:text-6xl font-serif text-gray-900 mb-4 group-hover:scale-105 transition-transform duration-300">{stat.v}</div>
                  <div className="text-base md:text-lg font-medium text-gray-900 mb-2">{stat.l}</div>
                  <div className="text-sm text-gray-600">{stat.desc}</div>
                </div>

                {/* Decorative corner */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gray-300 group-hover:border-gray-400 transition-colors duration-300" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gray-300 group-hover:border-gray-400 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services - Modern Imagery Cards */}
      <section className="py-24 bg-gradient-to-b from-[#395958] to-[#2a4342] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-20"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#F1D291] mb-6">Our Expertise</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-8 leading-tight">
              Bespoke Wedding<br />
              <span className="text-white/80">Services</span>
            </h2>
            <p className="text-base md:text-lg text-white/80 max-w-4xl mx-auto leading-relaxed">
              From the first spark of inspiration to the final dance, we orchestrate every element with precision, artistry, and an unwavering commitment to your vision.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Concept & Design',
                desc: 'Transforming your vision into a cohesive design story that captures the essence of your love',
                img: '/img/i19.jpg',
              },
              {
                title: 'Vendor Curation',
                desc: 'Handpicked partners who share our commitment to excellence and artistic vision',
                img: '/img/i20.jpg',
              },
              {
                title: 'Day-of Coordination',
                desc: 'Seamless execution so you can be fully present in every precious moment',
                img: '/img/i21.jpg',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: '-100px' }}
                className="group relative overflow-hidden rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm shadow-[0_25px_50px_rgba(0,0,0,0.3)] hover:shadow-[0_35px_70px_rgba(0,0,0,0.4)] transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/50 group-hover:from-black/20 group-hover:to-black/60 transition-all duration-500" />

                  {/* Floating icon */}
                  <div className="absolute top-6 left-6">
                    <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                      <div className="w-6 h-6 bg-black rounded-full"></div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-xl md:text-2xl font-serif text-white mb-4 leading-tight">{item.title}</h3>
                  <p className="text-sm md:text-base text-white/80 leading-relaxed mb-6">{item.desc}</p>

                  <motion.a
                    href="/#services"
                    className="group/link inline-flex items-center gap-3 text-[#F1D291] text-[11px] tracking-[0.3em] uppercase hover:text-white transition-colors duration-300"
                    whileHover={{ x: 5 }}
                  >
                    <span>Explore Service</span>
                    <svg className="w-4 h-4 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.a>
                </div>

                {/* Decorative corner element */}
                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* Journey - Modern Timeline */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="uppercase tracking-[0.3em] text-xs text-gray-500">Your Journey</div>
            <h2 className="text-3xl md:text-4xl font-serif text-black mt-2">From Dream to Reality</h2>
            <p className="text-sm text-gray-600 max-w-3xl mx-auto mt-4">
              Every love story is unique, and your wedding should be too. We understand your vision, craft a plan, curate the perfect team, and deliver a day that lives forever.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-24 left-0 right-0 h-px bg-black/10" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { s: '01', t: 'Consultation', d: 'Understanding your vision and preferences', img: '/pin9.jpg' },
                { s: '02', t: 'Design & Plan', d: 'Concepts, moodboards, and detailed timelines', img: '/pin10.jpg' },
                { s: '03', t: 'Curate Vendors', d: 'Selecting best-fit partners worldwide', img: '/pin11.jpg' },
                { s: '04', t: 'Execute & Host', d: 'Flawless delivery of your dream wedding', img: '/pin12.jpg' },
              ].map((step, i) => (
                <motion.div
                  key={step.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="group relative overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_20px_40px_rgba(0,0,0,0.06)]"
                >
                  <div className="absolute -top-4 left-6 z-10">
                    <div className="px-3 py-1 rounded-full bg-black text-white text-[10px] tracking-[0.3em]">{step.s}</div>
                  </div>
                  <div className="relative h-44">
                    <img src={step.img} alt={step.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/40" />
                  </div>
                  <div className="p-6">
                    <div className="text-lg font-serif text-black">{step.t}</div>
                    <p className="mt-2 text-sm text-gray-600">{step.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Press />

      <DestinationsMarquee />

      {/* CTA */}
      <CTA />
    </div>
  );
}